import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { apiClient } from '@/core/api/client';
import type { ApiResponse } from '@/core/api/types';
import { decodeJwt, parseCurrentUser, isExpired } from './jwt';
import { exchangeForFirebaseToken, signInWithFirebaseToken } from './firebaseExchange';
import {
  AUTH_UNAUTHORIZED_EVENT,
  TOKEN_STORAGE_KEY,
  type CurrentUser,
  type LoginRequest,
  type LoginResponseData,
} from './types';
import { AuthContext } from './authContextDef';
import { buildDevToken, DEV_LOGIN_ENABLED, type TestRole } from './devLogin';

/** Reads the token from localStorage and restores state if it is still valid. */
function restoreSession(): { token: string; user: CurrentUser } | null {
  const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!raw) return null;

  const claims = decodeJwt(raw);
  if (!claims || isExpired(claims)) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }

  return { token: raw, user: parseCurrentUser(claims) };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => restoreSession()?.token ?? null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(
    () => restoreSession()?.user ?? null
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
    setCurrentUser(null);
    if (auth) {
      // Fire-and-forget — we don't need to await sign-out to clear local state.
      void signOut(auth).catch((err) => {
        console.warn('[auth] Firebase signOut failed.', err);
      });
    }
  }, []);

  // Listen for 401 events dispatched by the axios interceptor (client.ts).
  // The custom-event bridge avoids a circular import between client.ts and this
  // context module.
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
  }, [logout]);

  const login = useCallback(async (req: LoginRequest) => {
    const res = await apiClient.post<ApiResponse<LoginResponseData>>(
      '/api/v1/Login/login',
      { email: req.email, password: req.password }
    );

    const { token: newToken } = res.data.data;
    const claims = decodeJwt(newToken);
    if (!claims) {
      throw new Error('Received a malformed token from the server.');
    }

    // Persist before setting state so the axios interceptor can pick it up
    // immediately for the Firebase exchange request below.
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    setToken(newToken);
    setCurrentUser(parseCurrentUser(claims));

    // Firebase custom-token exchange — non-blocking; failures are logged but
    // never propagated so a missing endpoint doesn't break login.
    try {
      const customToken = await exchangeForFirebaseToken();
      if (customToken) {
        await signInWithFirebaseToken(customToken);
      }
    } catch (err) {
      console.warn('[auth] Firebase exchange step failed (non-fatal).', err);
    }
  }, []);

  const loginAsTestUser = useCallback(async (role: TestRole) => {
    // Phase 1.5 GCP migration: previously synthesised a fake JWT signed with
    // a placeholder, which the .NET API rejected → instant 401 → logout on
    // the first authed page navigation. Now hits the real backend with the
    // matching seeded `colt-{role}@southernstarmga.com` account so we get a
    // real JWT and Firebase token. The buildDevToken path is dead code that
    // still exists for tests; remove once unused.
    void buildDevToken;
    const emailByRole: Record<TestRole['userType'], string> = {
      Administrator: 'colt@southernstarmga.com',
      Adjuster:      'colt-adjuster@southernstarmga.com',
      Customer:      'colt-customer@southernstarmga.com',
      TowProvider:   'colt-towprovider@southernstarmga.com',
      Driver:        'colt-driver@southernstarmga.com',
    };
    await login({ email: emailByRole[role.userType], password: 'BigDryFish432!' });
  }, [login]);

  const selectRole = useCallback(async (userId: string | number) => {
    const res = await apiClient.get<ApiResponse<LoginResponseData>>(
      '/api/v1/Login/LoginV2',
      { params: { userId } }
    );

    const { token: newToken } = res.data.data;
    const claims = decodeJwt(newToken);
    if (!claims) {
      throw new Error('Received a malformed token from LoginV2.');
    }

    // Persist before updating state so the axios interceptor picks it up
    // immediately for any requests triggered by the subsequent navigation.
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    setToken(newToken);
    setCurrentUser(parseCurrentUser(claims));

    // TODO(ui-generator): Firestore rules in Phase 1 are scoped by userId /
    // sourceTypeId, not userType, so the existing Firebase session is valid
    // across role swaps.  If that assumption changes (e.g. per-role Firestore
    // rules), mirror the non-blocking exchange pattern from login() here.
  }, []);

  const value = useMemo(
    () => ({
      token,
      currentUser,
      isAuthenticated: token !== null && currentUser !== null,
      login,
      logout,
      selectRole,
      ...(DEV_LOGIN_ENABLED ? { loginAsTestUser } : {}),
    }),
    [token, currentUser, login, logout, selectRole, loginAsTestUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
